/**
 * Filtro de stock de la tabla de productos: todos, con, sin o un rango.
 *
 * "Con" y "Sin" se aplican al elegirlos. El rango necesita que se escriban
 * los dos extremos, asi que abre dos campos y se aplica con su boton (o con
 * Enter): aplicar a cada tecla pediria la tabla por cada digito.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { modoStock } from '../../utilidades/filtros-productos';
import type { FiltrosProductos, ModoStock } from '../../utilidades/filtros-productos';

export interface FiltroStockProps {
  filtros: FiltrosProductos;
  onCambiar(rango: { stockMin: number | undefined; stockMax: number | undefined }): void;
}

function cantidadDe(texto: string): number | undefined {
  if (texto.trim() === '') return undefined;
  const valor = Math.floor(Number(texto));
  return Number.isFinite(valor) && valor >= 0 ? valor : undefined;
}

export function FiltroStock({ filtros, onCambiar }: FiltroStockProps) {
  const modo = modoStock(filtros);
  // Elegir "Rango" todavia no filtra nada: solo abre los campos.
  const [rangoAbierto, setRangoAbierto] = useState(false);
  const [desde, setDesde] = useState(filtros.stockMin?.toString() ?? '');
  const [hasta, setHasta] = useState(filtros.stockMax?.toString() ?? '');

  // Si el filtro cambio desde afuera (un chip quitado, "Limpiar todo"), los
  // campos muestran lo vigente y no lo que quedo escrito.
  useEffect(() => {
    setRangoAbierto(false);
    setDesde(filtros.stockMin?.toString() ?? '');
    setHasta(filtros.stockMax?.toString() ?? '');
  }, [filtros.stockMin, filtros.stockMax]);

  const valorSelector: ModoStock = rangoAbierto ? 'rango' : modo;
  const conRango = valorSelector === 'rango';

  function elegir(nuevo: ModoStock): void {
    if (nuevo === 'rango') {
      setRangoAbierto(true);
      return;
    }
    setRangoAbierto(false);
    if (nuevo === 'con') onCambiar({ stockMin: 1, stockMax: undefined });
    else if (nuevo === 'sin') onCambiar({ stockMin: undefined, stockMax: 0 });
    else onCambiar({ stockMin: undefined, stockMax: undefined });
  }

  function aplicarRango(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const minimo = cantidadDe(desde);
    const maximo = cantidadDe(hasta);
    // Escrito al reves se entiende igual: se dan vuelta los extremos.
    if (minimo !== undefined && maximo !== undefined && minimo > maximo) {
      onCambiar({ stockMin: maximo, stockMax: minimo });
    } else {
      onCambiar({ stockMin: minimo, stockMax: maximo });
    }
  }

  return (
    <div className="filtro filtro-stock">
      <label className="filtro-et" htmlFor="filtro-stock">
        Stock
      </label>
      <select
        id="filtro-stock"
        className={modo !== 'todos' ? 'selector activo' : 'selector'}
        value={valorSelector}
        onChange={(evento) => {
          elegir(evento.target.value as ModoStock);
        }}
      >
        <option value="todos">Todos</option>
        <option value="con">Con stock</option>
        <option value="sin">Sin stock</option>
        <option value="rango">Rango...</option>
      </select>

      {conRango ? (
        <form className="rango-stock" onSubmit={aplicarRango}>
          <label>
            <span className="solo-lectores">Stock desde</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className="entrada entrada-numero"
              placeholder="Desde"
              value={desde}
              onChange={(evento) => {
                setDesde(evento.target.value);
              }}
            />
          </label>
          <span aria-hidden="true">a</span>
          <label>
            <span className="solo-lectores">Stock hasta</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className="entrada entrada-numero"
              placeholder="Hasta"
              value={hasta}
              onChange={(evento) => {
                setHasta(evento.target.value);
              }}
            />
          </label>
          <button type="submit" className="btn btn-sm btn-primario">
            Aplicar
          </button>
        </form>
      ) : null}
    </div>
  );
}
